import React from 'react';
import styled from 'styled-components';
import { useLocalStorageState } from 'ahooks';
import {
  Layout,
  Row,
  Col,
  Flex,
  Typography,
  Card,
  Switch,
  Button,
  Divider,
  List,
} from 'antd';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;

export const StyledWrapper = styled(Layout)`
  overflow: hidden;
  position: relative;
  height: max-content;
  min-height: 100%;
`;

export const StyledContent = styled(Content)`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  margin-bottom: 2rem;
`;

export const StyledFeatureCaption = styled(Flex)`
  padding: 2.4rem 0;
`;

export const BETA_TOGGLES = {
  notifications: {
    key: 'betaNotificationsEnabled',
  },
  mainMenu: {
    key: 'betaMainMenuEnabled',
  },
};

export function Beta() {
  const [showBetaNotifications, setShowBetaNotifications] =
    useLocalStorageState<boolean>(BETA_TOGGLES.notifications.key, {
      defaultValue: false,
      listenStorageChange: true,
    });

  const [showBetaMainMenu, setShowBetaMainMenu] = useLocalStorageState<boolean>(
    BETA_TOGGLES.mainMenu.key,
    {
      defaultValue: false,
      listenStorageChange: true,
    }
  );

  const handleOnBetaNotificationsToggleChange = (checked: boolean) => {
    setShowBetaNotifications(checked);
    if (!checked) {
      setShowBetaMainMenu(false);
    }
  };

  const handleOnBetaMainMenuToggleChange = (checked: boolean) => {
    setShowBetaMainMenu(checked);
  };

  return (
    <StyledWrapper>
      <StyledContent>
        <Row>
          <Col span={24}>
            <StyledFeatureCaption justify="flex-start" vertical>
              <Title level={3} style={{ fontWeight: 400, margin: 0 }}>
                Beta Features
              </Title>
              <Paragraph type="secondary">
                This page offers access to experimental features in beta
                testing, giving Beta Testing Program participants early access
                to new features before their official release.
              </Paragraph>
            </StyledFeatureCaption>
          </Col>
          <Col span={24}>
            <Card>
              <Row justify="space-between" align="middle" gutter={[16, 16]}>
                <Col md={16} sm={24} xs={24}>
                  <Title level={5} style={{ marginTop: 0 }}>
                    Beta Access Notifications
                  </Title>
                  <Paragraph type="secondary">
                    Enable this option to receive notifications whenever beta
                    features become available in specific parts of the
                    application. Once enabled, you’ll start seeing in-app alerts
                    when new beta features are ready for testing.
                  </Paragraph>
                </Col>
                <Col md={8} sm={24} xs={24}>
                  <Flex gap={16} align="end" vertical>
                    <Flex align="center" gap={16}>
                      <Text>Show Beta Notifications</Text>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        checked={showBetaNotifications}
                        onChange={handleOnBetaNotificationsToggleChange}
                      />
                    </Flex>
                  </Flex>
                </Col>
              </Row>
              <Row
                align="middle"
                gutter={[16, 16]}
                justify="space-between"
                style={{ marginTop: 16 }}
              >
                <Col md={16} sm={24} xs={24}>
                  <Title level={5} style={{ marginTop: 0 }}>
                    Share Your Feedback
                  </Title>
                  <Paragraph type="secondary">
                    Your feedback helps us improve beta features before their
                    official release. Let us know what you think!
                  </Paragraph>
                </Col>
                <Col md={8} sm={24} xs={24}>
                  <Flex gap={16} align="end" vertical>
                    <Button type="primary" disabled>
                      Share Feedback
                    </Button>
                  </Flex>
                </Col>
              </Row>
              {showBetaNotifications && (
                <>
                  <Row align="middle">
                    <Col span={24}>
                      <Divider style={{ margin: '16px 0' }} />
                    </Col>
                  </Row>
                  <Row
                    align="middle"
                    gutter={[16, 16]}
                    justify="space-between"
                    style={{ marginTop: 16 }}
                  >
                    <Col md={16} sm={24} xs={24}>
                      <Title level={5} style={{ marginTop: 0 }}>
                        Beta Features Available for Testing
                      </Title>
                      <Paragraph type="secondary">
                        Explore the latest beta features available for testing.
                        Help us improve these features by trying them out and
                        providing feedback.
                      </Paragraph>
                    </Col>
                    <Col md={24} sm={24} xs={24}>
                      <List bordered>
                        <List.Item
                          key={BETA_TOGGLES.mainMenu.key}
                          actions={[
                            <Flex align="center" gap={16}>
                              <Text>Try It</Text>
                              <Switch
                                checkedChildren="On"
                                unCheckedChildren="Off"
                                checked={showBetaMainMenu}
                                onChange={handleOnBetaMainMenuToggleChange}
                              />
                            </Flex>,
                          ]}
                        >
                          <Flex vertical>
                            <Text strong>
                              Main Menu & Top Header & SideMenu
                            </Text>
                            <Paragraph type="secondary">
                              Switch to the new menu beta version to test out
                              the fresh look. This version includes a new layout
                              with a collapsible side menu, improved navigation,
                              and enhanced performance.
                            </Paragraph>
                          </Flex>
                        </List.Item>
                      </List>
                    </Col>
                  </Row>
                </>
              )}
            </Card>
          </Col>
        </Row>
      </StyledContent>
    </StyledWrapper>
  );
}
